// src/components/suppliers/SupplierTable.tsx
import React, { useEffect, useState, type ReactNode } from "react";
import {
  FiEdit2,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiFilter,
  FiCheckCircle,
  FiKey,
} from "react-icons/fi";

import Input from "@/components/shared/ui/Input";


import {
  type SupplierRow,
  type SupplierFilters,
} from "@/pages/suppliers/useSuppliersPage";

/* ============================================================================
   Small table helpers
============================================================================ */
function Th({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <th className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wide ${className}`}>
      {children}
    </th>
  );
}

function Td({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <td className={`px-5 py-3.5 ${className}`}>{children}</td>;
}

/* Compact column filter popover */
function ColumnFilter({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (!t.closest?.("[data-popover]")) setOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  return (
    <div className="relative" data-popover>
      <button
        type="button"
        className="inline-flex items-center justify-center rounded p-0.5 text-gray-500 hover:text-gray-700"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Filter ${label}`}
        title={`Filter ${label}`}
      >
        <FiFilter />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-2 w-64 rounded-xl border border-gray-200 bg-white p-3 shadow-lg">
          <div className="mb-2 text-xs font-semibold text-gray-700">{label} Filter</div>
          <div className="space-y-2">{children}</div>
          <div className="mt-3 text-right">
            <button
              className="rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
              onClick={() => setOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* Initials helper (for manager avatar) */
const initials = (a?: string, b?: string) =>
  `${(a?.[0] || "").toUpperCase()}${(b?.[0] || "").toUpperCase()}`;

/* ============================================================================
   Component
============================================================================ */

export default function SupplierTable({
  rows,
  serialStart,
  filters,
  setFilters,
  page,
  setPage,
  pageSize,
  totalItems,
  totalPages,
  onEdit,
  onManageModules,
  onDelete,
  onApprove,
  approvingId,
}: {
  rows: SupplierRow[]; // pageRows
  serialStart: number;
  filters: SupplierFilters;
  setFilters: React.Dispatch<React.SetStateAction<SupplierFilters>>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  onEdit: (row: SupplierRow) => void;
  onManageModules: (row: SupplierRow) => void;
  onDelete: (row: SupplierRow) => void;
  onApprove: (row: SupplierRow) => void;
  approvingId?: number | string | null;
}) {
  const start = serialStart;

  const statusBadge = (status: SupplierRow["status"]) => {
    if (status === "APPROVED") {
      return "inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700";
    }
    if (status === "REJECTED") {
      return "inline-flex rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700";
    }
    return "inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700";
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr className="border-b border-gray-200">
              <Th>S.No</Th>

              {/* Company */}
              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Company</span>
                  <ColumnFilter label="Company">
                    <Input
                      placeholder="Contains…"
                      value={filters.company || ""}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, company: e.target.value }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              {/* Manager */}
              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Manager</span>
                  <ColumnFilter label="Manager">
                    <Input
                      placeholder="Name contains…"
                      value={filters.manager || ""}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, manager: e.target.value }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              {/* Reservation Email */}
              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Reservation Email</span>
                  <ColumnFilter label="Reservation Email">
                    <Input
                      placeholder="Contains…"
                      value={filters.reservationEmail || ""}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          reservationEmail: e.target.value,
                        }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              {/* Emergency Phone */}
              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Emergency Phone</span>
                  <ColumnFilter label="Emergency Phone">
                    <Input
                      placeholder="Contains…"
                      value={filters.emergencyPhone || ""}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          emergencyPhone: e.target.value,
                        }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              {/* Address line 1 */}
              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Address Line 1</span>
                  <ColumnFilter label="Address Line 1">
                    <Input
                      placeholder="Contains…"
                      value={filters.addressLine1 || ""}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          addressLine1: e.target.value,
                        }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              {/* Address line 2 */}
              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Address Line 2</span>
                  <ColumnFilter label="Address Line 2">
                    <Input
                      placeholder="Contains…"
                      value={filters.addressLine2 || ""}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          addressLine2: e.target.value,
                        }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              {/* Country */}
              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Country</span>
                  <ColumnFilter label="Country">
                    <Input
                      placeholder="Contains…"
                      value={filters.country || ""}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, country: e.target.value }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              {/* State */}
              <Th>
                <div className="flex items-center gap-1.5">
                  <span>State</span>
                  <ColumnFilter label="State">
                    <Input
                      placeholder="Contains…"
                      value={filters.state || ""}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, state: e.target.value }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              {/* City */}
              <Th>
                <div className="flex items-center gap-1.5">
                  <span>City</span>
                  <ColumnFilter label="City">
                    <Input
                      placeholder="Contains…"
                      value={filters.city || ""}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, city: e.target.value }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>

              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Status</span>
                  <ColumnFilter label="Status">
                    <select
                      value={filters.status || ""}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          status: e.target.value as SupplierFilters["status"],
                        }))
                      }
                      className="w-full rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm text-gray-800"
                    >
                      <option value="">All</option>
                      <option value="PENDING">Pending</option>
                      <option value="APPROVED">Approved</option>
                      <option value="REJECTED">Rejected</option>
                    </select>
                  </ColumnFilter>
                </div>
              </Th>

              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={12} className="px-5 py-10 text-center text-sm text-gray-500">
                  No suppliers found.
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={r.id} className="hover:bg-gray-50/60">
                  <Td className="text-gray-700">{start + i + 1}</Td>

                  {/* Company */}
                  <Td className="text-gray-900">{r.companyName}</Td>

                  {/* Manager */}
                  <Td className="text-gray-900">
                    <div className="flex items-center gap-2">
                      <div className="grid h-8 w-8 place-items-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
                        {initials(r.managerFirstName, r.managerLastName)}
                      </div>
                      <span>
                        {r.managerFirstName} {r.managerLastName}
                      </span>
                    </div>
                  </Td>

                  {/* Reservation / Emergency */}
                  <Td className="text-gray-700">{r.reservationEmail || "—"}</Td>
                  <Td className="text-gray-700">{r.emergencyPhone || "—"}</Td>

                  {/* Address */}
                  <Td className="text-gray-700">{r.addressLine1 || "—"}</Td>
                  <Td className="text-gray-700">{r.addressLine2 || "—"}</Td>

                  {/* Country / State / City */}
                  <Td className="text-gray-700">{r.country || "—"}</Td>
                  <Td className="text-gray-700">{r.state || "—"}</Td>
                  <Td className="text-gray-700">{r.city || "—"}</Td>
                  <Td>
                    <span className={statusBadge(r.status)}>
                      {r.status}
                    </span>
                  </Td>

                  <Td>
                    <div className="flex items-center justify-end gap-1.5">
                      {r.status === "PENDING" && (
                        <button
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                          onClick={() => onApprove(r)}
                          aria-label="Approve"
                          title="Approve"
                          disabled={String(approvingId ?? "") === String(r.id)}
                        >
                          <FiCheckCircle />
                        </button>
                      )}
                      <button
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                        onClick={() => onManageModules(r)}
                        aria-label="Manage module access"
                        title="Manage module access"
                      >
                        <FiKey />
                      </button>
                      <button
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                        onClick={() => onEdit(r)}
                        aria-label="Edit"
                        title="Edit"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                        onClick={() => onDelete(r)}
                        aria-label="Delete"
                        title="Delete"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile list */}
      <div className="md:hidden divide-y divide-gray-200">
        {rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">No suppliers found.</div>
        ) : (
          rows.map((r, i) => (
            <div key={r.id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {start + i + 1}. {r.companyName}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500">
                    Manager: {r.managerFirstName} {r.managerLastName}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500">
                    {r.addressLine1 || "—"}
                    {r.city ? ` • ${r.city}` : ""}
                  </div>
                  <div className="mt-1">
                    <span className={statusBadge(r.status)}>{r.status}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {r.status === "PENDING" && (
                    <button
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                      onClick={() => onApprove(r)}
                      aria-label="Approve"
                      disabled={String(approvingId ?? "") === String(r.id)}
                    >
                      <FiCheckCircle />
                    </button>
                  )}
                  <button
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                    onClick={() => onManageModules(r)}
                    aria-label="Manage module access"
                  >
                    <FiKey />
                  </button>
                  <button
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                    onClick={() => onEdit(r)}
                    aria-label="Edit"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                    onClick={() => onDelete(r)}
                    aria-label="Delete"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-700">
                <div>
                  <span className="text-gray-500">Reservation:</span>{" "}
                  {r.reservationEmail || "—"}
                </div>
                <div>
                  <span className="text-gray-500">Emergency:</span>{" "}
                    {r.emergencyPhone || "—"}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination footer */}
      <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-4 py-3">
        <div className="text-xs text-gray-500">
          Showing{" "}
          <span className="font-medium text-gray-700">
            {totalItems === 0 ? 0 : start + 1}
          </span>{" "}
          to{" "}
          <span className="font-medium text-gray-700">
            {Math.min(start + pageSize, totalItems)}
          </span>{" "}
          of{" "}
          <span className="font-medium text-gray-700">{totalItems}</span> results
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <FiChevronLeft /> Prev
          </button>

          {/* Numbered buttons */}
          <div className="hidden items-center gap-1 sm:flex">
            {Array.from({ length: Math.min(totalPages, 6) }).map((_, idx) => {
              const n = idx + 1;
              const active = n === page;
              return (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  className={`rounded-md px-2.5 py-1.5 text-sm ${
                    active
                      ? "bg-sky-600 text-white"
                      : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {n}
                </button>
              );
            })}
            {totalPages > 6 && (
              <span className="px-1 text-sm text-gray-500">…</span>
            )}
          </div>

          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Next <FiChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
}
